import { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Col, Button, Spin, Radio, Tooltip } from 'antd'
// eslint-disable-next-line import/no-extraneous-dependencies
import { InfoCircleOutlined } from '@ant-design/icons'

import { TitleAndDivider } from '@components'
import { useConfigurationProperties } from '@hooks'
import { RoleEnum } from '@types'
import { useUser } from '@contexts'

import { StyledRow, Container, StyledText, StyledInputNumber } from './components'
import { validationSchema, FormType } from './validations'

const Settings = () => {
  const { state } = useUser()
  const roles = state?.roles.map(role => role.name)
  const { ADMINRO } = RoleEnum
  const { t } = useTranslation()
  const {
    allocationMinutes,
    allocationId,
    isLoading,
    deleteTicketsDays,
    deleteTicketsId,
    assignmentTicketsId,
    assignmentTickets,
    preAuthTicketsId,
    preAuthTickets,
    updateConfigPropsTime,
    updateConfigPropsPercentaje,
    updateConfigPropsAssignmentTickets,
    isConfigLoadingTime,
    isConfigLoadingPercentaje,
    updatePreAuthTickets,
  } = useConfigurationProperties()
  const { control, handleSubmit, setValue } = useForm<FormType>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      allocation: 0,
      ticketDays: 0,
      assignmentTickets: 'false',
      preAuthTickets: 'false',
    },
  })
  const [newAllocationTime, setNewAllocationTime] = useState<number>()
  const [newDeleteTicketDays, setNewDeleteTicketDays] = useState<number>()

  const handleAllocationUpdate = (form: FormType) => {
    if (allocationId && allocationId !== newAllocationTime)
      updateConfigPropsTime({ id: allocationId, value: form.allocation })
  }

  const handleDeleteTicketsUpdate = (form: FormType) => {
    if (deleteTicketsId && deleteTicketsId !== newDeleteTicketDays)
      updateConfigPropsPercentaje({ id: deleteTicketsId, value: form.ticketDays })
  }
  const handleAssignmentTicketsUpdate = (value: number | undefined) => {
    updateConfigPropsAssignmentTickets({
      id: assignmentTicketsId,
      value: value === 1 ? 'true' : 'false',
    })
  }
  const handlePreauth = (value: number | undefined) => {
    updatePreAuthTickets({
      id: preAuthTicketsId,
      value: value === 1 ? 'true' : 'false',
    })
  }
  useEffect(() => {
    if (allocationMinutes) setValue('allocation', allocationMinutes)
    setNewAllocationTime(allocationMinutes)
    if (deleteTicketsDays) setValue('ticketDays', deleteTicketsDays)
    setNewDeleteTicketDays(deleteTicketsDays)
  }, [allocationMinutes, deleteTicketsDays, setValue])

  const handleAllocationChange = (value: any) => {
    setValue('allocation', value as number)
    setNewAllocationTime(value as number)
  }

  const handleDeleteTicketsChange = (value: any) => {
    setValue('ticketDays', value as number)
    setNewDeleteTicketDays(value as number)
  }

  return (
    <Container>
      <TitleAndDivider title={t('admin_router.admin_dashboard.settings.title')} />

      <Spin spinning={isLoading || isConfigLoadingTime || isConfigLoadingPercentaje}>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.assignation_time')}:{' '}
              <Tooltip
                title={t(
                  'admin_router.admin_dashboard.settings.time_for_assignment_without_category',
                )}
              >
                <InfoCircleOutlined style={{ marginLeft: '2px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          <Col span={2}>
            <Controller
              name="allocation"
              control={control}
              defaultValue={allocationMinutes}
              render={({ field }) => (
                <StyledInputNumber
                  min={0}
                  value={field.value}
                  onChange={handleAllocationChange}
                  disabled={roles?.includes(ADMINRO)}
                />
              )}
            />
          </Col>
          <Col span={3}>
            <Button
              onClick={handleSubmit(handleAllocationUpdate)}
              disabled={
                allocationMinutes === newAllocationTime ||
                newAllocationTime === null ||
                roles?.includes(ADMINRO)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>{t('admin_router.admin_dashboard.settings.ticket_days')}:</StyledText>
          </Col>
          <Col span={2}>
            {roles?.includes(ADMINRO) ? (
              <Controller
                name="ticketDays"
                control={control}
                defaultValue={deleteTicketsDays}
                render={({ field }) => (
                  <StyledInputNumber
                    min={0}
                    max={100}
                    value={field.value}
                    onChange={handleDeleteTicketsChange}
                    disabled
                  />
                )}
              />
            ) : (
              <Controller
                name="ticketDays"
                control={control}
                defaultValue={deleteTicketsDays}
                render={({ field }) => (
                  <StyledInputNumber
                    min={0}
                    max={100}
                    value={field.value}
                    onChange={handleDeleteTicketsChange}
                  />
                )}
              />
            )}
          </Col>
          <Col span={3}>
            <Button
              onClick={handleSubmit(handleDeleteTicketsUpdate)}
              disabled={
                deleteTicketsDays === newDeleteTicketDays ||
                newDeleteTicketDays === null ||
                roles?.includes(ADMINRO)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.tickets_assignation')}:
              <Tooltip title={t('admin_router.admin_dashboard.settings.set_default_travel_option')}>
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          {roles?.includes(ADMINRO) ? (
            <Radio.Group size="large" value={assignmentTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio
                  value={1}
                  onChange={e => handleAssignmentTicketsUpdate(e.target.value)}
                  disabled
                >
                  {t('admin_router.admin_dashboard.settings.tickets_assig_automatic')}
                </Radio>
                <Radio
                  value={0}
                  onChange={e => handleAssignmentTicketsUpdate(e.target.value)}
                  disabled
                >
                  {t('admin_router.admin_dashboard.settings.tickets_assig_manual')}
                </Radio>
              </Col>
            </Radio.Group>
          ) : (
            <Radio.Group size="large" value={assignmentTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} onChange={e => handleAssignmentTicketsUpdate(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.tickets_assig_automatic')}
                </Radio>
                <Radio value={0} onChange={e => handleAssignmentTicketsUpdate(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.tickets_assig_manual')}
                </Radio>
              </Col>
            </Radio.Group>
          )}
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.preauth_tickets')}:
              <Tooltip title={t('admin_router.admin_dashboard.settings.pre_authorization_meaning')}>
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          {roles?.includes(ADMINRO) ? (
            <Radio.Group size="large" value={preAuthTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} onChange={e => handlePreauth(e.target.value)} disabled>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} onChange={e => handlePreauth(e.target.value)} disabled>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          ) : (
            <Radio.Group size="large" value={preAuthTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} onChange={e => handlePreauth(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} onChange={e => handlePreauth(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          )}
        </StyledRow>
      </Spin>
    </Container>
  )
}
export { Settings }
